import {
  Badge,
  Box,
  Flex,
  FormLabel,
  HStack,
  Input,
  Link,
  Switch,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { FormRootError } from "@/common/components/Form/FormRootError";
import FormSubmit from "@/common/components/Form/FormSubmit";
import { ManagedFormControl } from "@/common/components/Form/ManagedFormControl";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useErrorToast } from "@/common/hooks/useErrorToast";
import { translateError } from "@/common/utils/errorHandling";
import { segmentQueryKeys } from "@/company/data/segmentQueryKeys";
import { environmentQueryKeys } from "@/environment/data/environmentQueryKeys";
import { featureQueryKeys } from "@/feature/data/featureQueryKeys";
import { useLaunchDarklyImportMutation } from "@/integration/data/useLaunchDarklyImportMutation";
import {
  ImportResponse,
  ldImportSchema,
  ldImportSchemaType,
} from "@reflag/shared/importAPI";

import { ImportPreviewModal } from "./ImportPreviewModal";

export function ImportLaunchDarklyForm() {
  const form = useForm<ldImportSchemaType>({
    resolver: zodResolver(ldImportSchema),
    mode: "onChange",
    defaultValues: {
      ldApiToken: "",
      ldProjectId: "default",
      euRegion: false,
      dryRun: true,
    },
  });

  const toast = useToast();
  const errorToast = useErrorToast();
  const previewModal = useDisclosure();
  const queryClient = useQueryClient();
  const { app, env } = useEnvironment();
  const [importPreview, setImportPreview] = useState<ImportResponse | null>(
    null,
  );

  const previewMutation = useLaunchDarklyImportMutation();
  const importMutation = useLaunchDarklyImportMutation();

  const handlePrepareImport = async (data: ldImportSchemaType) => {
    try {
      const result = await previewMutation.mutateAsync({
        ...data,
        dryRun: true,
      });
      setImportPreview(result);
      previewModal.onOpen();
    } catch (error) {
      const { title, description } = translateError(
        error,
        "Failed to fetch LaunchDarkly flags",
      );
      errorToast({ title, description });
    }
  };

  const handleConfirmImport = async () => {
    const formData = form.getValues();
    try {
      const result = await importMutation.mutateAsync({
        ...formData,
        dryRun: false,
      });

      previewModal.onClose();
      setImportPreview(null);

      const flagCount = result.created?.featureIds.length ?? 0;
      const segmentCount = result.created?.segmentIds.length ?? 0;
      const envCount = result.created?.environmentIds.length ?? 0;

      // Invalidate queries for features, segments, and environments
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: featureQueryKeys.list(app.id, env.id),
        }),
        queryClient.invalidateQueries({
          queryKey: featureQueryKeys.listNames(app.id),
        }),
        queryClient.invalidateQueries({
          queryKey: segmentQueryKeys.list(app.id),
        }),
        queryClient.invalidateQueries({
          queryKey: segmentQueryKeys.metadata(app.id),
        }),
        queryClient.invalidateQueries({
          queryKey: segmentQueryKeys.counts(app.id, env.id),
        }),
        ...(envCount > 0
          ? [
              queryClient.invalidateQueries({
                queryKey: environmentQueryKeys.list(app.id),
              }),
            ]
          : []),
      ]);

      toast({
        title: "Import successful",
        description: `Imported ${flagCount} ${flagCount === 1 ? "flag" : "flags"}${segmentCount > 0 ? ` and ${segmentCount} ${segmentCount === 1 ? "segment" : "segments"}` : ""}`,
        status: "success",
        duration: 5000,
      });

      // Reset the form
      form.reset();
    } catch (error) {
      const { title, description } = translateError(
        error,
        "Failed to import flags",
      );
      errorToast({ title, description });
    }
  };

  const handleCloseModal = () => {
    previewModal.onClose();
    setImportPreview(null);
  };

  return (
    <>
      <Flex direction="column" gap={4} maxW="mediumForm">
        <Box>
          <HStack pb={1}>
            <FormLabel m={0}>LaunchDarkly Import</FormLabel>
            <Badge colorScheme="purple" variant="subtle">
              Beta
            </Badge>
          </HStack>
          <Text color="dimmed" fontSize="sm" mb={4}>
            Enter your details below to import your LaunchDarkly flags and
            targeting information.
          </Text>

          <Box>
            <FormProvider {...form}>
              <form onSubmit={form.handleSubmit(handlePrepareImport)}>
                <VStack align={"flex-start"} spacing={4}>
                  <ManagedFormControl
                    label="LaunchDarkly Project ID"
                    name="ldProjectId"
                    render={({ field }) => <Input {...field} size="md" />}
                  />
                  <ManagedFormControl
                    label="LaunchDarkly API Token"
                    name="ldApiToken"
                    render={({ field }) => (
                      <>
                        <Input {...field} size="md" autoFocus />
                        <Text color="dimmed" fontSize="sm" mb={4}>
                          Find your tokens and create a new one in the{" "}
                          <Link
                            href="https://app.launchdarkly.com/settings/authorization"
                            target="_blank"
                            isExternal
                          >
                            LaunchDarkly settings
                          </Link>
                        </Text>
                      </>
                    )}
                  />

                  <ManagedFormControl
                    label="EU Region"
                    name="euRegion"
                    render={({ field }) => <Switch {...field} size="md" />}
                  />
                  <FormRootError />

                  <FormSubmit
                    isLoading={previewMutation.isPending}
                    loadingText="Preparing..."
                  >
                    Prepare import
                  </FormSubmit>
                </VStack>
              </form>
            </FormProvider>
          </Box>
        </Box>
      </Flex>

      <ImportPreviewModal
        importData={importPreview}
        isImporting={importMutation.isPending}
        isOpen={previewModal.isOpen}
        onClose={handleCloseModal}
        onConfirmImport={handleConfirmImport}
      />
    </>
  );
}
